import React, { useEffect, useState } from 'react';
import {
  Box, Button, IconButton, Typography, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import {BACKEND_BASE_URL} from '../config'

const API_BASE_URL = `${BACKEND_BASE_URL}/api/capture/tags/`;

const MeetingTags = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [newTag, setNewTag] = useState('');

  const [editRowId, setEditRowId] = useState(null);
  const [editTagValue, setEditTagValue] = useState('');

  const token = localStorage.getItem('authToken');

  const getAuthHeaders = () => {
    if (!token) return null;
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line
  }, []);

  const fetchTags = async () => {
  if (!token) {
    console.error('No auth token found');
    setTags([]);
    return;
  }
  setLoading(true);
  try {
    const response = await fetch(API_BASE_URL, { headers: getAuthHeaders() });
    const data = await response.json();
    console.log("Fetched tag list result:", data);
    setTags(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error('Failed to fetch tags:', error);
    setTags([]);
  } finally {
    setLoading(false);
  }
};


  const handleCreateTag = async () => {
    if (!token) {
      alert('Authentication token is missing. Please login again.');
      return;
    }
    if (!newTag.trim()) return;

    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: newTag.trim() }),
      });
      if (response.ok) {
        setNewTag('');
        setOpen(false);
        fetchTags();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to create tag:', errorData);
        alert(errorData.detail || JSON.stringify(errorData) || 'Failed to create tag. Please check your input.');
      }
    } catch (error) {
      console.error('Error creating tag:', error);
      alert('Error creating tag. Please try again.');
    }
  };

  const handleEditClick = (tag) => {
    setEditRowId(tag.id);
    setEditTagValue(tag.name);
  };

  const handleSaveClick = async (id) => {
    if (!token) {
      alert('Authentication token is missing. Please login again.');
      return;
    }
    if (!editTagValue.trim()) {
      alert('Tag name cannot be empty.');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}${id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: editTagValue.trim() }),
      });
      if (response.ok) {
        setEditRowId(null);
        setEditTagValue('');
        fetchTags();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error updating tag:', errorData);
        alert(errorData.detail || 'Failed to update tag. Please try again.');
      }
    } catch (error) {
      console.error('Error updating tag:', error);
      alert('Error updating tag. Please try again.');
    }
  };

  const handleDeleteClick = async (id) => {
    if (!token) {
      alert('Authentication token is missing. Please login again.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this tag?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}${id}/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        fetchTags();
      } else {
        console.error('Error deleting tag, status:', response.status);
        alert('Failed to delete tag. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      alert('Error deleting tag. Please try again.');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Meeting Tags
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Create Tag
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1E2A78' }}>
            <TableRow>
              <TableCell sx={{ color: '#fff' }}>S.No.</TableCell>
              <TableCell sx={{ color: '#fff' }}>Meeting Tag</TableCell>
              <TableCell sx={{ color: '#fff' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : Array.isArray(tags) && tags.length > 0 ? (
              tags.map((tag, index) => (
                <TableRow key={tag.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {editRowId === tag.id ? (
                      <TextField
                        size="small"
                        value={editTagValue}
                        onChange={(e) => setEditTagValue(e.target.value)}
                        fullWidth
                      />
                    ) : (
                      tag.name
                    )}
                  </TableCell>
                  <TableCell>
                    {editRowId === tag.id ? (
                      <>
                        <IconButton
                          color="primary"
                          onClick={() => handleSaveClick(tag.id)}
                          disabled={!editTagValue.trim()}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setEditRowId(null);
                            setEditTagValue('');
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          color="primary"
                          onClick={() => handleEditClick(tag)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(tag.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No meeting tags found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Dialog for Creating New Tag */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create New Meeting Tag</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Tag Name"
            fullWidth
            margin="normal"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTag}
            disabled={!newTag.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingTags;
